import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { articles as staticArticles } from '../data/siteData';
import { blogImages } from '../assets';
import { getPublicArticles } from '../services/contentApi';
import './ArticleDetail.css';

/* ─── Helpers ─────────────────────────────────────────────────────── */

const MONTHS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function normalizeApiArticle(a) {
  return {
    id:        a.id,
    slug:      a.slug,
    title:     a.titre,
    category:  a.categorie || '',
    excerpt:   a.resume    || '',
    contenu:   a.contenu   || '',
    imageSrc:  a.image_url || null,
    author:    'Rédaction TTA',
    dateLabel: formatDate(a.published_at),
    tags:      Array.isArray(a.tags) ? a.tags : [],
  };
}

/* ─── Component ───────────────────────────────────────────────────── */

export default function ArticleDetail() {
  const { slug } = useParams();
  const [apiArticles, setApiArticles] = useState(null);

  useEffect(() => {
    getPublicArticles().then(data => {
      if (Array.isArray(data)) setApiArticles(data);
    });
  }, []);

  // Chercher l'article : API d'abord, puis fallback siteData
  let article     = null;
  let isApiSource = false;

  if (apiArticles) {
    const found = apiArticles.find(a => a.slug === slug);
    if (found) { article = normalizeApiArticle(found); isApiSource = true; }
  }

  if (!article) {
    const found = staticArticles.find(a => a.slug === slug);
    if (found) article = found;
  }

  // Articles en sidebar
  const sidebarArticles = apiArticles
    ? apiArticles.filter(a => a.slug !== slug).slice(0, 4).map(normalizeApiArticle)
    : staticArticles.filter(a => a.slug !== slug).slice(0, 4);

  if (!article) {
    return (
      <main className="article-notfound">
        <h1>Article introuvable</h1>
        <Link to="/actualites" className="btn-primary">← Retour aux actualités</Link>
      </main>
    );
  }

  const heroImg   = isApiSource ? article.imageSrc : blogImages[article.image];
  // HTML contenu rédigé via TipTap admin — sanitisé avec DOMPurify avant rendu
  const safeHtml  = isApiSource ? DOMPurify.sanitize(article.contenu) : '';

  return (
    <main className="article-page">

      {/* HERO */}
      <section className="article-hero">
        <div className="article-hero__img-wrap">
          <img src={heroImg} alt={article.title} className="article-hero__img" />
          <div className="article-hero__overlay" />
        </div>
        <div className="article-hero__content">
          <div className="article-hero__breadcrumb">
            <Link to="/actualites">Actualités</Link>
            <span>/</span>
            <span>{article.category}</span>
          </div>
          <span className="article-hero__cat">{article.category}</span>
          <h1 className="article-hero__title">{article.title}</h1>
          <div className="article-hero__meta">
            <div className="article-hero__author">
              <div className="article-hero__avatar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <span>{article.author}</span>
            </div>
            <span className="article-hero__sep">·</span>
            <span>{article.dateLabel}</span>
            {article.readTime && (
              <><span className="article-hero__sep">·</span><span>{article.readTime}</span></>
            )}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <div className="article-layout">
        <article className="article-body">

          {isApiSource ? (
            <div
              className="article-html-content"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          ) : (
            <>
              {article.sections.map((s, i) => {
                if (s.type === 'intro') return <p key={i} className="article-intro">{s.text}</p>;
                if (s.type === 'h2')   return <h2 key={i} className="article-h2">{s.text}</h2>;
                if (s.type === 'p')    return <p key={i} className="article-p">{s.text}</p>;
                if (s.type === 'ul')   return (
                  <ul key={i} className="article-ul">
                    {s.items.map((item, j) => <li key={j}>{item}</li>)}
                  </ul>
                );
                return null;
              })}
            </>
          )}

          {/* TAGS */}
          <div className="article-tags">
            {(article.tags || []).map(t => (
              <span key={t} className="actu-tag">{t}</span>
            ))}
          </div>
        </article>

        {/* SIDEBAR */}
        <aside className="article-sidebar">
          <div className="article-sidebar__card">
            <h4>Autres articles</h4>
            <div className="article-sidebar__list">
              {sidebarArticles.map(a => {
                const thumb = a.imageSrc || blogImages[a.image];
                return (
                  <Link to={`/actualites/${a.slug}`} key={a.id} className="article-sidebar__item">
                    <img src={thumb} alt={a.title} className="article-sidebar__thumb" />
                    <div>
                      <div className="article-sidebar__cat">{a.category}</div>
                      <div className="article-sidebar__title">{a.title}</div>
                      <div className="article-sidebar__date">{a.dateLabel}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="article-sidebar__cta">
            <h4>Intéressé par nos formations ?</h4>
            <p>Rejoignez TTA et devenez acteur de la transformation digitale africaine.</p>
            <Link to="/formations" className="btn-primary" style={{ fontSize: '13px', padding: '11px 20px', justifyContent: 'center' }}>
              Voir les formations →
            </Link>
          </div>
        </aside>
      </div>

      {/* BACK */}
      <div className="article-back-wrap">
        <Link to="/actualites" className="article-back">← Retour aux actualités</Link>
      </div>

    </main>
  );
}
